import React, { useCallback, useMemo, useState } from 'react';
import { isEmpty } from 'lodash';

import ChatSearchField from '~/components/AiChat/components/ChatSearchField';
import type { ChatHistory } from '~/components/AiChat/controller/types';
import { useChatHistoryEntries } from '~/components/AiChat/hooks/useChatHistoryEntries';
import { useChatMixpanel } from '~/components/AiChat/hooks/useChatMixpanel';
import { groupHistoryByRelativeTime } from '~/components/AiChat/utils/groupHistoryByRelativeTime';
import { Text } from '~/components/core';
import LoadingSwitch from '~/components/core/Loading/LoadingSwitch';
import { CLIVE_CHAT_MIXPANEL_EVENTS } from '~/pocs/mixpanel';
import { reportAxiosError } from '~/Utils';
import cn from '~/Utils/cn';

import { ChatHistoryItem } from './ChatHistoryItem';
import { RenameChatDialog } from './RenameChatDialog';
import { SearchEmptyState } from './SearchEmptyState';

interface RenamingChat {
  id: string;
  title: string;
}

interface HistoryViewContainerProps {
  history: ChatHistory;
  className?: string;
  containerClassName?: string;
  onChatSelect: (chatId: string) => Promise<void>;
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  showSearchField?: boolean;
  showRename?: boolean;
}

export const HistoryViewContainer: React.FC<HistoryViewContainerProps> = ({
  history,
  className,
  containerClassName,
  onChatSelect,
  searchQuery,
  onSearchQueryChange,
  showSearchField = true,
  showRename = true,
}) => {
  const { trackChatEvent } = useChatMixpanel();
  const { entries, setEntries, isLoading, isError } = useChatHistoryEntries(history);
  const { rename, untitledTitle, analyticsIdOf } = history;
  const [renamingChat, setRenamingChat] = useState<RenamingChat | null>(null);
  const [isRenaming, setIsRenaming] = useState(false);

  const handleChatSelect = useCallback(
    async (chatId: string) => {
      await onChatSelect(chatId);
    },
    [onChatSelect]
  );

  const handleOpenRenameDialog = useCallback((chatId: string, currentTitle: string) => {
    setRenamingChat({ id: chatId, title: currentTitle });
  }, []);

  const handleCloseRenameDialog = useCallback(() => {
    setRenamingChat(null);
  }, []);

  const handleRenameSubmit = useCallback(
    async (newTitle: string) => {
      if (!renamingChat || !rename) return;

      try {
        setIsRenaming(true);
        await rename(renamingChat.id, newTitle);

        trackChatEvent(CLIVE_CHAT_MIXPANEL_EVENTS.CHAT_RENAME_SAVED, { new_title_length: newTitle.length });

        setEntries((prev) => prev.map((chat) => (chat.id === renamingChat.id ? { ...chat, title: newTitle } : chat)));

        setRenamingChat(null);
      } catch (error) {
        reportAxiosError(error);
      } finally {
        setIsRenaming(false);
      }
    },
    [rename, renamingChat, setEntries, trackChatEvent]
  );

  const filteredHistory = useMemo(() => {
    if (!searchQuery.trim()) {
      return entries;
    }
    const query = searchQuery.toLowerCase();
    return entries.filter((item) => {
      const title = item.title || untitledTitle(item.id);
      return title.toLowerCase().includes(query);
    });
  }, [entries, searchQuery, untitledTitle]);

  const groupedHistory = useMemo(() => groupHistoryByRelativeTime(filteredHistory), [filteredHistory]);

  return (
    <div className={cn('__HISTORY_VIEW__ flex h-full flex-col overflow-hidden', className)}>
      {showSearchField && !isEmpty(entries) && (
        <div className="bg-blue-100 px-20 py-12">
          <ChatSearchField value={searchQuery} onChange={onSearchQueryChange} />
        </div>
      )}

      <div className={cn('flex flex-1 flex-col gap-16 overflow-y-auto px-32 py-24', containerClassName)}>
        <LoadingSwitch isLoading={isLoading} isError={isError}>
          {isEmpty(entries) && (
            <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
              No chat history found
            </Text>
          )}

          {!isEmpty(entries) && isEmpty(filteredHistory) ? <SearchEmptyState /> : null}

          {groupedHistory.map(({ groupLabel, items }) => (
            <div key={groupLabel} className="flex flex-col gap-8">
              <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD}>
                {groupLabel}
              </Text>

              {items.map((chat) => (
                <ChatHistoryItem
                  key={chat.id}
                  analyticsId={analyticsIdOf(chat.id)}
                  createdAt={chat.createdAt}
                  displayTitle={chat.title || untitledTitle(chat.id)}
                  onClick={() => handleChatSelect(chat.id)}
                  onRenameClick={() => handleOpenRenameDialog(chat.id, chat.title || untitledTitle(chat.id))}
                  showRename={showRename && !!rename}
                />
              ))}
            </div>
          ))}
        </LoadingSwitch>
      </div>

      {renamingChat && (
        <RenameChatDialog
          open={true}
          currentTitle={renamingChat.title}
          isLoading={isRenaming}
          onClose={handleCloseRenameDialog}
          onSubmit={handleRenameSubmit}
        />
      )}
    </div>
  );
};
